import PropTypes from 'prop-types';
import React, { useState } from 'react';

import Flatpickr from 'react-flatpickr';
import { DebounceInput } from 'react-debounce-input';
import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

import {
  Badge, Button, Checkbox, Drawer, InfoCircle, Plus, Radio, RadioGroup, Select, Tabs, TextInput, Toggle, Tooltip, XClose,
} from '@opendate-app/setlist';

import CurrencyInput from '../../CurrencyInput/components/CurrencyInput';
import ImageUpload from '../../../shared/ImageUpload';
import TicketDrawerSection from './TicketDrawerSection';
import PriceBreakoutAccordion from './PriceBreakoutAccordion';
import DynamicPricingSection from './DynamicPricingSection';
import { ACCESSIBLE_SEATING_OPTIONS, moveFocusWithoutSelectingPriceType } from './ticketTypeFormHelpers';
import {
  addOnIsSeated, isSharingInventory, minPerOrderMoreThanMax, parseDateTime, requiredAddOnOptions, showActivityOption,
  visiblePricePoints,
} from './addOnFormHelpers';

import './TicketDrawer.css';
import './AddOnDrawer.css';

const ADD_ON_DRAWER_V2_FORM_ID = 'add-on-drawer-v2-form';

const PRICE_TYPE_TABS = [
  { value: 'paid', label: 'Paid' },
  { value: 'free', label: 'Free' },
];

const AVAILABILITY_OPTIONS = [
  { value: 'Based on show details', label: 'Based on show details' },
  { value: 'Date & Time', label: 'Date & Time' },
];

const SALES_CHANNEL_OPTIONS = [
  { value: 'Everywhere', label: 'Everywhere' },
  { value: 'Online only', label: 'Online only' },
  { value: 'At the door only', label: 'At the door only' },
];

const CHECK_IN_PERIOD_OPTIONS = [
  { value: 'all_time', label: 'All Time' },
  { value: 'daily', label: 'Daily' },
];

const DATE_FORMAT = 'M j, Y at h:iK';

const formatDateTime = (value) => {
  const date = parseDateTime(value);
  return date
    ? date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : null;
};

const RequiredLabel = ({ children }) => (
  <>
    {children}
    <span className="ticket-drawer-required">&nbsp;*</span>
  </>
);

const LabelWithInfo = ({ tip, children }) => (
  <span className="ticket-drawer-label-with-info">
    {children}
    <Tooltip title={tip} maxWidth={240}>
      <span className="ticket-drawer-info" tabIndex={0} aria-label="More information">
        <InfoCircle width={14} height={14} aria-hidden="true" />
      </span>
    </Tooltip>
  </span>
);

const RadioLabel = ({ title, note }) => (
  <span className="add-on-drawer-radio-label">
    <span>{title}</span>
    <span className="ticket-drawer-muted-text">{note}</span>
  </span>
);

const DateField = ({ id, label, value, disabled, minDate, maxDate, onClose }) => (
  <div className="ticket-drawer-field">
    <label htmlFor={id} className="ticket-drawer-field-label">
      <RequiredLabel>{label}</RequiredLabel>
    </label>
    <Flatpickr
      id={id}
      options={{ dateFormat: DATE_FORMAT, enableTime: true, minDate, maxDate }}
      disabled={disabled}
      placeholder="Select date and time"
      className={`form-control ticketing-date-picker form-control-flatpickr${disabled ? ' form-control-disabled' : ''}`}
      style={{ fontSize: '14px' }}
      value={value}
      onClose={onClose}
    />
  </div>
);

const AddOnDrawer = ({
  addOnForForm,
  addOns,
  confirm,
  team,
  showActivities,
  ticketTypes,
  manualTaxes,
  manualTaxShouldBeChecked,
  canSave,
  closeOnEsc,
  onChange,
  onRecalculate,
  onImageSelected,
  onImageRemove,
  onRemovePricePoint,
  onSave,
  onClose,
  renderSalesWindow,
  renderCheckInWindow,
  salesWindowSummary: salesWindowSummaryOverride,
  nameError,
}) => {
  const [free, setFree] = useState(() => parseFloat(String(addOnForForm.base_price_two_decimals ?? '').replace(/,/g, '')) === 0);
  const seated = addOnIsSeated(addOnForForm);
  const accessible = seated && Boolean(addOnForForm.accessible_seating);
  const useDateAndTime = addOnForForm.availability === 'Date & Time';
  const pricePoints = visiblePricePoints(addOnForForm.price_points);

  const merge = (attrs) => onChange({ ...addOnForForm, ...attrs });
  const mergeAndRecalc = (attrs) => {
    const updated = { ...addOnForForm, ...attrs };
    onChange(updated);
    onRecalculate(updated);
  };

  const [paidSnapshot, setPaidSnapshot] = useState(null);

  const changePriceType = (priceType) => {
    const isFree = priceType === 'free';
    if (isFree === free) return;
    if (isFree) {
      setPaidSnapshot({
        base_price_two_decimals: addOnForForm.base_price_two_decimals,
        price_points: addOnForForm.price_points,
      });
    }
    setFree(isFree);
    mergeAndRecalc(isFree ? {
      base_price_two_decimals: '0.00',
      round_up: false,
      absorb_fees: false,
      dynamic_pricing_enabled: false,
      price_points: (addOnForForm.price_points || [])
        .filter((pricePoint) => !pricePoint.newRecord)
        .map((pricePoint) => ({ ...pricePoint, _destroy: true })),
    } : (paidSnapshot || { base_price_two_decimals: '' }));
  };

  const updatePricePoint = (pricePoint, attrs, recalc = false) => {
    const updated = {
      ...addOnForForm,
      price_points: addOnForForm.price_points.map((pp) => (pp.id === pricePoint.id ? { ...pp, ...attrs } : pp)),
    };
    onChange(updated);
    if (recalc) onRecalculate(updated);
  };

  const addPricePoint = () => merge({
    price_points: [
      ...(addOnForForm.price_points || []),
      { id: Date.now() + Math.random(), newRecord: true, starts_at: '', price_two_decimals: '' },
    ],
  });

  const startShowActivity = showActivities.find((showActivity) => showActivity.id === addOnForForm.start_show_activity_id);
  const endShowActivity = showActivities.find((showActivity) => showActivity.id === addOnForForm.end_show_activity_id);
  const showActivityOptions = showActivities.map((showActivity) => showActivityOption(showActivity));
  const pricePointsMinDate = useDateAndTime ? addOnForForm.start_time : startShowActivity?.start_time;
  const pricePointsMaxDate = useDateAndTime ? addOnForForm.end_time : endShowActivity?.start_time;

  const ticketTypeOptions = ticketTypes
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((ticketType) => ({ value: ticketType.id, label: ticketType.name }));
  const selectedTicketTypeIds = ticketTypes
    // String compare: ids may arrive as strings, and the series wizard keys ticket types by name.
    .filter((ticketType) => (addOnForForm.ticket_type_ids || []).some((id) => String(id) === String(ticketType.id)))
    .map((ticketType) => ticketType.id);

  // The series wizard passes no add-ons; series plans can't require one.
  // Seated add-ons are picked on the seat map, so they can't wait on another add-on.
  const addOnRequirementOptions = seated ? [] : requiredAddOnOptions(addOns, addOnForForm);
  const transferable = addOnForForm.transferable ?? true;
  const resellable = addOnForForm.transferable ?? false;
  const scans = String(addOnForForm.check_in_limit ?? '0');
  const checkInPeriod = addOnForForm.check_in_period || 'all_time';
  const hasImage = Boolean(addOnForForm.image_preview_url || addOnForForm.image_url);

  const defaultRoundUp = Boolean(confirm?.venue_ownership?.default_round_up_add_ons);
  const feesEdited = Boolean(addOnForForm.absorb_fees || Boolean(addOnForForm.round_up) !== defaultRoundUp);
  const feesSummary = [
    addOnForForm.round_up ? 'Round up on' : 'No round up',
    addOnForForm.absorb_fees ? 'Fees absorbed' : 'Fans pay fees',
    addOnForForm.dynamic_pricing_enabled ? 'Dynamic pricing on' : null,
  ].filter(Boolean).join(' · ');

  const rulesEdited = Boolean(
    addOnForForm.require_ticket_type ||
      addOnForForm.require_add_on ||
      addOnForForm.transferable != null ||
      accessible ||
      Number(addOnForForm.minimum_per_order) !== 1 ||
      Number(addOnForForm.maximum_per_order) !== 10 ||
      (addOnForForm.sales_channel && addOnForForm.sales_channel !== 'Everywhere')
  );
  const rulesSummary = [
    addOnForForm.require_ticket_type ? 'Ticket holders only' : (addOnForForm.require_add_on ? null : 'Anyone'),
    addOnForForm.require_add_on ? 'Requires an add-on' : null,
    `${addOnForForm.minimum_per_order || '–'}–${addOnForForm.maximum_per_order || '–'} per order`,
    addOnForForm.sales_channel && addOnForForm.sales_channel !== 'Everywhere' ? addOnForForm.sales_channel : null,
    transferable ? 'Transferable' : 'Not transferable',
    accessible ? 'Accessible seating' : null,
    resellable && !accessible ? 'Resellable' : null,
  ].filter(Boolean).join(' · ');

  const salesWindowEdited = useDateAndTime || pricePoints.length > 0;
  const salesWindowSummary = salesWindowSummaryOverride || [
    useDateAndTime
      ? `${formatDateTime(addOnForForm.start_time) || 'Start not set'} → ${formatDateTime(addOnForForm.end_time) || 'End not set'}`
      : `${startShowActivity?.name || 'Start not set'} → ${endShowActivity?.name || 'End not set'}`,
    pricePoints.length > 0 ? `${pricePoints.length} price change${pricePoints.length === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(' · ');

  const fanEdited = Boolean(addOnForForm.description || addOnForForm.confirmation_details || hasImage);
  const showDescription = !seated || isSharingInventory(addOns, addOnForForm);
  const fanSummary = [
    showDescription ? (addOnForForm.description ? 'Description' : 'No description') : null,
    addOnForForm.confirmation_details ? 'Email note' : 'No email note',
    hasImage ? 'Image' : 'No image',
  ].filter(Boolean).join(' · ');

  const defaultCheckInPeriod = confirm?.check_in_period || 'all_time';
  const checkInEdited = Boolean(
    addOnForForm.timed_entry ||
      addOnForForm.hide_qr_code ||
      Number(scans) !== 1 ||
      checkInPeriod !== defaultCheckInPeriod
  );
  const checkInSummary = [
    scans === '0' ? 'Unlimited scans' : `${scans} scan${scans === '1' ? '' : 's'}`,
    checkInPeriod === 'daily' ? 'Daily' : 'All time',
    addOnForForm.timed_entry ? 'Timed entry' : null,
    addOnForForm.hide_qr_code ? 'QR hidden' : 'QR shown',
  ].filter(Boolean).join(' · ');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (canSave) onSave();
  };

  return (
    <Drawer
      open
      backdrop
      closeOnEsc={closeOnEsc}
      width={600}
      data-lift-conductor-orb
      title={addOnForForm.id ? 'Edit Add-on' : 'Add Add-on'}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form={ADD_ON_DRAWER_V2_FORM_ID} disabled={!canSave}>
            Save
          </Button>
        </>
      }
    >
      <form id={ADD_ON_DRAWER_V2_FORM_ID} className="ticket-drawer" onSubmit={handleSubmit}>
        <div onKeyDownCapture={moveFocusWithoutSelectingPriceType}>
          <Tabs
            variant="button-border"
            size="sm"
            collapseBelow={0}
            aria-label="Add-on price type"
            items={PRICE_TYPE_TABS}
            value={free ? 'free' : 'paid'}
            onChange={changePriceType}
          />
        </div>

        <div className="ticket-drawer-top">
          <TextInput
            id="add-on-name"
            label={<RequiredLabel>Name</RequiredLabel>}
            placeholder="VIP Parking"
            error={nameError || undefined}
            value={addOnForForm.base_name || ''}
            onChange={(e) => merge({ base_name: e.target.value })}
          />

          <div className="ticket-drawer-field-row">
            <div className="ticket-drawer-field">
              <TextInput
                as={MaskedInput}
                id="add-on-available"
                label={<RequiredLabel>Available Quantity</RequiredLabel>}
                mask={createNumberMask({ prefix: '', decimalLimit: 0 })}
                placeholder="50"
                disabled={seated}
                onChange={(e) => merge({ available: e.target.value })}
                value={addOnForForm.available || ''}
              />
            </div>
            <div className="ticket-drawer-field">
              {free ? (
                <TextInput id="add-on-price" label={<RequiredLabel>Price</RequiredLabel>} value="Free" disabled />
              ) : (
                <TextInput
                  as={CurrencyInput}
                  id="add-on-price"
                  label={<RequiredLabel>Price</RequiredLabel>}
                  leadingText="$"
                  maskOptions={{ prefix: '', integerLimit: null }}
                  value={addOnForForm.base_price_two_decimals || ''}
                  onChange={(e) => mergeAndRecalc({ base_price_two_decimals: e.target.value })}
                />
              )}
            </div>
          </div>

          {!free ? <PriceBreakoutAccordion item={addOnForForm} /> : null}
        </div>

        <div className="ticket-drawer-sections">
          {!free ? (
            <TicketDrawerSection title="Fees" summary={feesSummary} edited={feesEdited}>
              <div className="form-group">
                <Checkbox
                  id="add-on-round-up"
                  label="Round up to the nearest dollar"
                  checked={addOnForForm.round_up || false}
                  onChange={(e) => mergeAndRecalc({ round_up: e.target.checked })}
                />
              </div>
              <div className="form-group">
                <Checkbox
                  id="add-on-absorb-fees"
                  label="Absorb fees — deduct them from your revenue"
                  checked={addOnForForm.absorb_fees || false}
                  onChange={(e) => mergeAndRecalc({ absorb_fees: e.target.checked })}
                />
              </div>
              {manualTaxes.length > 0 ? (
                <div className="form-group">
                  <p className="ticket-drawer-group-title">Apply Taxes</p>
                  {manualTaxes
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((manualTax) => (
                      <div key={manualTax.id} className="ticket-drawer-check-row">
                        <Checkbox
                          id={`manual-tax-${manualTax.id}`}
                          label={`${manualTax.name} (${parseFloat(manualTax.rate)}%)`}
                          checked={manualTaxShouldBeChecked(addOnForForm, manualTax)}
                          onChange={(e) => {
                            const taxableItems = [...(addOnForForm.taxable_items || [])];
                            const foundIndex = taxableItems.findIndex((taxableItem) => taxableItem.manual_tax_id === manualTax.id);

                            if (e.target.checked) {
                              if (foundIndex === -1) {
                                taxableItems.push({ manual_tax_id: manualTax.id, _destroy: false });
                              } else {
                                taxableItems[foundIndex] = { ...taxableItems[foundIndex], _destroy: false };
                              }
                            } else if (foundIndex !== -1) {
                              taxableItems[foundIndex] = { ...taxableItems[foundIndex], _destroy: true };
                            }

                            mergeAndRecalc({ taxable_items: taxableItems });
                          }}
                        />
                      </div>
                    ))}
                </div>
              ) : null}
              <DynamicPricingSection ticketTypeForForm={addOnForForm} onTicketTypeChange={onChange} team={team} />
            </TicketDrawerSection>
          ) : null}

          <TicketDrawerSection title="Who can buy" summary={rulesSummary} edited={rulesEdited}>
            <div className="form-group">
              <RadioGroup
                name="add-on-require-ticket-type"
                value={addOnForForm.require_ticket_type ? 'ticket' : 'any'}
                onChange={(value) => merge({ require_ticket_type: value === 'ticket', ticket_type_ids: [] })}
              >
                <Radio
                  id="add-on-rule-any"
                  value="any"
                  label={<RadioLabel title="Anyone" note="Fans can buy this on its own" />}
                />
                <Radio
                  id="add-on-rule-ticket"
                  value="ticket"
                  label={<RadioLabel title="Ticket holders only" note="Must be bought with a ticket" />}
                />
              </RadioGroup>
            </div>
            {addOnForForm.require_ticket_type ? (
              <>
                <div className="form-group">
                  <Select
                    id="add-on-ticket-type-ids"
                    label="Required Ticket(s)"
                    placeholder="Select"
                    multiple
                    searchable
                    clearable
                    options={ticketTypeOptions}
                    value={selectedTicketTypeIds}
                    onChange={(ticketTypeIds) => merge({ ticket_type_ids: ticketTypeIds || [] })}
                  />
                </div>
                <div className="form-group">
                  <Checkbox
                    id="add-on-limit-quantity-to-admission-count"
                    label="Limit quantity to admission count"
                    checked={addOnForForm.limit_quantity_to_admission_count !== false}
                    onChange={(e) => merge({ limit_quantity_to_admission_count: e.target.checked })}
                  />
                  <p className="ticket-drawer-muted-text">
                    Caps the add-on quantity at the number of admission tickets in the order. Turn off for table or group products.
                  </p>
                </div>
                <div className="form-group">
                  <Checkbox
                    id="add-on-disable-require-ticket-type-with-direct-link"
                    label="Skip the ticket requirement with a direct add-on link"
                    checked={addOnForForm.disable_require_ticket_type_with_direct_link || false}
                    onChange={(e) => merge({ disable_require_ticket_type_with_direct_link: e.target.checked })}
                  />
                </div>
              </>
            ) : null}
            {addOnRequirementOptions.length > 0 || addOnForForm.require_add_on ? (
              <>
                <div className="form-group">
                  <Checkbox
                    id="add-on-require-add-on"
                    label="Only available with purchase of another add-on"
                    checked={addOnForForm.require_add_on || false}
                    onChange={(e) => merge({ require_add_on: e.target.checked, required_add_on_ids: [] })}
                  />
                </div>
                {addOnForForm.require_add_on ? (
                  <div className="form-group">
                    <Select
                      id="add-on-required-add-on-ids"
                      label="Required Add-on(s)"
                      placeholder="Select"
                      multiple
                      searchable
                      clearable
                      options={addOnRequirementOptions}
                      value={addOnForForm.required_add_on_ids || []}
                      onChange={(addOnIds) => merge({ required_add_on_ids: addOnIds || [] })}
                    />
                  </div>
                ) : null}
              </>
            ) : null}
            <div className="ticket-drawer-field-row form-group">
              <div className="ticket-drawer-field">
                <TextInput
                  as={MaskedInput}
                  id="add-on-minimum-per-order"
                  label="Min per order"
                  error={minPerOrderMoreThanMax(addOnForForm) || undefined}
                  mask={createNumberMask({ prefix: '', decimalLimit: 0 })}
                  disabled={seated}
                  onChange={(e) => merge({ minimum_per_order: e.target.value })}
                  value={addOnForForm.minimum_per_order || ''}
                />
              </div>
              <div className="ticket-drawer-field">
                <TextInput
                  as={MaskedInput}
                  id="add-on-maximum-per-order"
                  label="Max per order"
                  error={minPerOrderMoreThanMax(addOnForForm) || undefined}
                  mask={createNumberMask({ prefix: '', decimalLimit: 0 })}
                  onChange={(e) => merge({ maximum_per_order: e.target.value })}
                  value={addOnForForm.maximum_per_order || ''}
                />
              </div>
            </div>
            <div className="form-group">
              <Select
                id="add-on-sales-channel"
                label="Sales Channel"
                options={SALES_CHANNEL_OPTIONS}
                value={addOnForForm.sales_channel || SALES_CHANNEL_OPTIONS[0].value}
                onChange={(salesChannel) => merge({ sales_channel: salesChannel })}
              />
            </div>
            <div className="form-group">
              <Checkbox
                id="add-on-transferable"
                label="Fans can transfer"
                checked={transferable}
                onChange={(e) => merge({ transferable: e.target.checked ? null : false })}
              />
            </div>
            <div className="form-group mb-0">
              <Checkbox
                id="add-on-resellable"
                label="Fans can resell"
                checked={resellable && !accessible}
                disabled={accessible}
                onChange={(e) => merge({ transferable: e.target.checked ? true : null })}
                aria-describedby={seated ? 'add-on-resellable-seat-note' : undefined}
              />
              {accessible ? (
                <p id="add-on-resellable-seat-note" className="ticket-drawer-muted-text">
                  Accessible and companion seats can never be resold.
                </p>
              ) : seated ? (
                <p id="add-on-resellable-seat-note" className="ticket-drawer-muted-text">
                  {resellable
                    ? "This seat is sold separately from its ticket. A resold ticket won't include it."
                    : 'This seat still goes with its ticket when the ticket is resold.'}
                </p>
              ) : null}
            </div>
            {seated ? (
            <div className="form-group mb-0">
              <Select
                id="add-on-accessible-seating"
                label="Accessible seating"
                options={ACCESSIBLE_SEATING_OPTIONS}
                value={addOnForForm.accessible_seating || ''}
                hint="Companion seats are sold alongside an accessible seat. Accessible and companion seats can't be resold."
                onChange={(accessibleSeating) => merge({ accessible_seating: accessibleSeating })}
              />
            </div>
            ) : null}
          </TicketDrawerSection>

          <TicketDrawerSection title="Sales Window" summary={salesWindowSummary} edited={salesWindowEdited} defaultOpen>
            {renderSalesWindow ? renderSalesWindow() : (
            <>
            <div className="form-group">
              <Select
                id="add-on-availability"
                label="When are add-ons available?"
                options={AVAILABILITY_OPTIONS}
                value={addOnForForm.availability || AVAILABILITY_OPTIONS[0].value}
                onChange={(availability) => merge({ availability })}
              />
            </div>
            <div className="ticket-drawer-field-row form-group">
              {useDateAndTime ? (
                <>
                  <DateField
                    id="add-on-start-date"
                    label="Start Date"
                    value={addOnForForm.start_time}
                    onClose={(dates) => {
                      const startTime = parseDateTime(dates[0]);
                      const endTime = parseDateTime(addOnForForm.end_time);
                      merge(endTime && endTime < startTime ? { start_time: startTime, end_time: '' } : { start_time: startTime });
                    }}
                  />
                  <DateField
                    id="add-on-end-date"
                    label="End Date"
                    value={addOnForForm.end_time}
                    minDate={addOnForForm.start_time}
                    disabled={!addOnForForm.start_time}
                    onClose={(dates) => {
                      const startTime = parseDateTime(addOnForForm.start_time);
                      const endTime = parseDateTime(dates[0]);
                      merge({ end_time: endTime && startTime && endTime < startTime ? '' : endTime });
                    }}
                  />
                </>
              ) : (
                <>
                  <div className="ticket-drawer-field">
                    <Select
                      id="add-on-start-show-activity"
                      label={<RequiredLabel>Start Show Detail</RequiredLabel>}
                      placeholder="Select a show detail"
                      options={showActivityOptions}
                      value={startShowActivity ? startShowActivity.id : null}
                      onChange={(showActivityId) => merge({ start_show_activity_id: showActivityId })}
                    />
                  </div>
                  <div className="ticket-drawer-field">
                    <Select
                      id="add-on-end-show-activity"
                      label={<RequiredLabel>End Show Detail</RequiredLabel>}
                      placeholder="Select a show detail"
                      options={showActivityOptions}
                      value={endShowActivity ? endShowActivity.id : null}
                      onChange={(showActivityId) => merge({ end_show_activity_id: showActivityId })}
                    />
                  </div>
                </>
              )}
            </div>
            {!free ? (
              <>
                {pricePoints.map((pricePoint, index) => (
                  <div key={pricePoint.id} className="add-on-drawer-price-point form-group">
                    <div className="add-on-drawer-price-point-header">
                      <Badge color="brand" size="sm">
                        Price Change {index + 1}
                      </Badge>
                      <Button
                        variant="tertiary"
                        size="sm"
                        icon={XClose}
                        aria-label={`Remove price change ${index + 1}`}
                        onClick={(e) => {
                          e.preventDefault();
                          onRemovePricePoint(pricePoint);
                        }}
                      />
                    </div>
                    <div className="add-on-drawer-price-point-fields">
                      <div className="ticket-drawer-field">
                        <TextInput
                          id={`price-point-${pricePoint.id}-name`}
                          label="Description"
                          value={pricePoint.name || ''}
                          placeholder="Advanced, Day of Show, etc..."
                          hint={`Displays as: ${addOnForForm.base_name || ''}${pricePoint.name ? ` - ${pricePoint.name}` : ''}`}
                          onChange={(e) => updatePricePoint(pricePoint, { name: e.target.value })}
                        />
                      </div>
                      <DateField
                        id={`price-point-${pricePoint.id}-start-date`}
                        label="Date"
                        value={parseDateTime(pricePoint.starts_at)}
                        minDate={parseDateTime(pricePointsMinDate || null)}
                        maxDate={parseDateTime(pricePointsMaxDate || null)}
                        onClose={(dates) => updatePricePoint(pricePoint, { starts_at: parseDateTime(dates[0]) })}
                      />
                      <div className="ticket-drawer-field add-on-drawer-price-point-price">
                        <TextInput
                          as={CurrencyInput}
                          id={`price-point-${pricePoint.id}-price`}
                          label={<RequiredLabel>Price</RequiredLabel>}
                          leadingText="$"
                          maskOptions={{ prefix: '' }}
                          value={pricePoint.price_two_decimals || ''}
                          placeholder="0.00"
                          onChange={(e) => updatePricePoint(pricePoint, { price_two_decimals: e.target.value }, true)}
                        />
                      </div>
                    </div>
                    <PriceBreakoutAccordion item={pricePoint} accordionId={`PricePoint${pricePoint.id}`} />
                  </div>
                ))}
                <div>
                  <Button variant="link-color" icon={Plus} onClick={addPricePoint}>
                    Add a price change
                  </Button>
                </div>
              </>
            ) : null}
            </>
            )}
          </TicketDrawerSection>

          <TicketDrawerSection title="What fans see" summary={fanSummary} edited={fanEdited}>
            {showDescription ? (
              <div className="form-group">
                <TextInput
                  as={DebounceInput}
                  element="textarea"
                  multiline
                  rows={3}
                  resize="none"
                  id="add-on-description"
                  label="Description"
                  hint="Shown on the event page and at checkout."
                  placeholder="What's included with this add-on?"
                  value={addOnForForm.description || ''}
                  onChange={(e) => merge({ description: e.target.value })}
                />
              </div>
            ) : null}
            <div className="form-group">
              <TextInput
                as={DebounceInput}
                element="textarea"
                multiline
                rows={3}
                resize="none"
                id="add-on-confirmation-details"
                label="Confirmation email note"
                hint="Only sent after purchase."
                placeholder="Pickup instructions, parking lot location, etc."
                value={addOnForForm.confirmation_details || ''}
                onChange={(e) => merge({ confirmation_details: e.target.value })}
              />
            </div>
            <ImageUpload
              imageUrl={addOnForForm.image_preview_url || addOnForForm.image_url}
              onUpload={onImageSelected}
              onDelete={onImageRemove}
              selectedFile={addOnForForm.image_file_info}
              placeholder={{
                title: 'Click to upload',
                subtitle: 'an image (optional)',
                formats: 'SVG, PNG, JPG or GIF (max. 800x400px)',
              }}
            />
          </TicketDrawerSection>

          <TicketDrawerSection title="Check-in" summary={checkInSummary} edited={checkInEdited}>
            <div className="ticket-drawer-field-row form-group">
              <div className="ticket-drawer-field">
                <TextInput
                  as={MaskedInput}
                  id="add-on-check-in-limit"
                  label="Scans per add-on"
                  hint="0 = unlimited"
                  mask={createNumberMask({ prefix: '', decimalLimit: 0 })}
                  value={addOnForForm.check_in_limit || '0'}
                  onChange={(e) => merge({ check_in_limit: e.target.value })}
                />
              </div>
              <div className="ticket-drawer-field">
                <Select
                  id="add-on-check-in-period"
                  label="Check-in Period"
                  hint="Daily resets at 4 AM venue time"
                  options={CHECK_IN_PERIOD_OPTIONS}
                  value={checkInPeriod}
                  onChange={(period) => merge({ check_in_period: period })}
                />
              </div>
            </div>
            {renderCheckInWindow ? renderCheckInWindow() : (
              <div className="form-group">
                <Toggle
                  id="add-on-timed-entry"
                  label="Timed Entry"
                  checked={addOnForForm.timed_entry || false}
                  onChange={(e) => merge({ timed_entry: e.target.checked })}
                />
              </div>
            )}
            {!renderCheckInWindow && addOnForForm.timed_entry ? (
              <>
                <div className="form-group">
                  <Select
                    id="add-on-timed-entry-availability"
                    label="When can add-ons be scanned?"
                    options={AVAILABILITY_OPTIONS}
                    value={addOnForForm.timed_entry_availability || AVAILABILITY_OPTIONS[0].value}
                    onChange={(timedEntryAvailability) => merge({ timed_entry_availability: timedEntryAvailability })}
                  />
                </div>
                <div className="ticket-drawer-field-row form-group">
                  {addOnForForm.timed_entry_availability === 'Date & Time' ? (
                    <>
                      <DateField
                        id="add-on-timed-entry-start-date"
                        label="Scan Start Time"
                        value={addOnForForm.timed_entry_start}
                        onClose={(dates) => {
                          const startTime = parseDateTime(dates[0]);
                          const endTime = parseDateTime(addOnForForm.timed_entry_end);
                          merge(endTime && endTime < startTime
                            ? { timed_entry_start: startTime, timed_entry_end: '' }
                            : { timed_entry_start: startTime });
                        }}
                      />
                      <DateField
                        id="add-on-timed-entry-end-date"
                        label="Scan End Time"
                        value={addOnForForm.timed_entry_end}
                        onClose={(dates) => merge({ timed_entry_end: parseDateTime(dates[0]) })}
                      />
                    </>
                  ) : (
                    <>
                      <div className="ticket-drawer-field">
                        <Select
                          id="add-on-timed-entry-start-show-activity"
                          label={<RequiredLabel>Scan Start Show Detail</RequiredLabel>}
                          placeholder="Select a show detail"
                          options={showActivityOptions}
                          value={addOnForForm.timed_entry_start_show_activity_id ?? null}
                          onChange={(showActivityId) => merge({ timed_entry_start_show_activity_id: showActivityId })}
                        />
                      </div>
                      <div className="ticket-drawer-field">
                        <Select
                          id="add-on-timed-entry-end-show-activity"
                          label={<RequiredLabel>Scan Stop Show Detail</RequiredLabel>}
                          placeholder="Select a show detail"
                          options={showActivityOptions}
                          value={addOnForForm.timed_entry_end_show_activity_id ?? null}
                          onChange={(showActivityId) => merge({ timed_entry_end_show_activity_id: showActivityId })}
                        />
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : null}
            <div className="form-group mb-0">
              <Toggle
                id="add-on-hide-qr-code"
                label={
                  <LabelWithInfo tip="Hide QR codes when this add-on won't need to be scanned">
                    Hide QR Code
                  </LabelWithInfo>
                }
                checked={addOnForForm.hide_qr_code || false}
                onChange={(e) => merge({ hide_qr_code: e.target.checked })}
              />
            </div>
          </TicketDrawerSection>
        </div>
      </form>
    </Drawer>
  );
};

AddOnDrawer.propTypes = {
  addOnForForm: PropTypes.object.isRequired,
  addOns: PropTypes.array,
  confirm: PropTypes.object.isRequired,
  team: PropTypes.object.isRequired,
  showActivities: PropTypes.array.isRequired,
  ticketTypes: PropTypes.array,
  manualTaxes: PropTypes.array,
  manualTaxShouldBeChecked: PropTypes.func.isRequired,
  canSave: PropTypes.bool,
  closeOnEsc: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onRecalculate: PropTypes.func,
  onImageSelected: PropTypes.func.isRequired,
  onImageRemove: PropTypes.func.isRequired,
  onRemovePricePoint: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  // Slots for the series wizard, whose windows are show details plus an
  // offset rather than ids or dates. Left out, the confirm's own editors render.
  renderSalesWindow: PropTypes.func,
  renderCheckInWindow: PropTypes.func,
  salesWindowSummary: PropTypes.string,
  nameError: PropTypes.string,
};

AddOnDrawer.defaultProps = {
  addOns: [],
  ticketTypes: [],
  manualTaxes: [],
  canSave: false,
  closeOnEsc: true,
  onRecalculate: () => {},
  renderSalesWindow: undefined,
  renderCheckInWindow: undefined,
  salesWindowSummary: undefined,
  nameError: undefined,
};

export default AddOnDrawer;
